import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Loader2, Save, ShieldCheck, Zap } from 'lucide-react';
import { toast } from "sonner";

interface FeatureConfig {
  enabled: boolean;
  minTier: 'FREE' | 'BASIC' | 'PRO' | 'AGENCY';
}

interface FeaturesConfig {
  [key: string]: FeatureConfig;
}

const FEATURE_LABELS: Record<string, { label: string, description: string }> = {
  grammar_check: {
    label: 'Grammar Check & Fix',
    description: 'Enables AI-powered grammar and spelling correction in the post editor.'
  },
  rephrase: {
    label: 'Text Rephrasing',
    description: 'Enables AI-powered text rephrasing to change tone or improve engagement.'
  },
  auto_translate: {
    label: 'Auto Translation',
    description: 'Smart auto-translation based on destination account language.'
  },
  steam_roadmap_opportunities: {
    label: 'Steam Fest Chances',
    description: 'Enables the Steam Fest Chances section on the dashboard to track upcoming sales and festivals.'
  },
  store_videos: {
    label: 'Store Videos',
    description: 'Allows users to keep local media files on the server after successful publication.'
  }
};

const TIERS = ['FREE', 'BASIC', 'PRO', 'AGENCY'];

export default function Features() {
  const [features, setFeatures] = useState<FeaturesConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  const fetchFeatures = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatures(response.data.features);
    } catch (error) {
      console.error('Error fetching features:', error);
      toast.error('Failed to load feature configurations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, []);

  const handleToggleFeature = (key: string, enabled: boolean) => {
    setFeatures(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [key]: { ...prev[key], enabled }
      };
    });
  };

  const handleTierChange = (key: string, minTier: any) => {
    setFeatures(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [key]: { ...prev[key], minTier }
      };
    });
  };

  const handleSave = async () => {
    if (!features) return;
    setSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/admin/settings/features`, features, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Feature configurations saved successfully.');
    } catch (error) {
      console.error('Error saving features:', error);
      toast.error('Failed to save feature configurations.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-card border-border text-foreground shadow-xl overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-primary to-cyan-500 w-full" />
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" />
              Feature Gating & Flags
            </CardTitle>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-primary hover:bg-primary/90 h-9 gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Changes
            </Button>
          </div>
          <CardDescription className="text-muted-foreground">
            Enable or disable system features globally and set minimum tier requirements for users.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {features && Object.entries(features).map(([key, config]) => {
            const labelInfo = FEATURE_LABELS[key] || { label: key, description: 'No description available.' };

            return (
              <div key={key} className="p-5 bg-muted/50 rounded-2xl border border-border space-y-4 transition-all hover:border-border">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Label className="text-base font-bold text-foreground">{labelInfo.label}</Label>
                      {config.enabled ? (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">
                          <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                          <span className="text-[10px] font-black text-green-500 uppercase">Active</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-card border border-border">
                          <span className="text-[10px] font-black text-muted-foreground uppercase">Disabled</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground max-w-xl">{labelInfo.description}</p>
                  </div>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(v) => handleToggleFeature(key, v)}
                  />
                </div>

                <div className="flex items-center gap-6 pt-2 border-t border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-card rounded-lg">
                      <Zap className="h-3.5 w-3.5 text-amber-400" />
                    </div>
                    <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Minimum User Tier</Label>
                  </div>

                  <Select
                    value={config.minTier}
                    onValueChange={(v) => handleTierChange(key, v)}
                    disabled={!config.enabled}
                  >
                    <SelectTrigger className="w-40 bg-background border-border h-9 text-xs">
                      <SelectValue placeholder="Select Tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border text-foreground">
                      {TIERS.map(tier => (
                        <SelectItem key={tier} value={tier}>{tier === 'PRO' ? 'INDIE' : tier}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            );
          })}

          {!features && (
            <div className="text-center py-10">
              <p className="text-muted-foreground text-sm">No feature configurations found.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
